import { ChangeDetectorRef, Component, ElementRef, OnDestroy, ViewChild } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { ErrorAlertComponent } from '../shared/error-alert/error-alert.component';
import { LoadingService } from '../../services/general/loading.service';
import { SariMetricsService } from '../../services/sari-metrics/sari-metrics.service';
import { SariMetricsResponse, PeriodoData, MotivoPorAgenteItem } from '../../interfaces/sari-metrics.interface';
import ApexCharts from 'apexcharts';
import jsPDF from 'jspdf';
import html2canvas from 'html2canvas';

@Component({
  selector: 'app-sari-metrics',
  standalone: true,
  imports: [CommonModule, FormsModule, ErrorAlertComponent],
  templateUrl: './sari-metrics.component.html',
  styleUrl: './sari-metrics.component.css'
})
export class SariMetricsComponent implements OnDestroy {
  @ViewChild('resultsContainer') resultsContainer!: ElementRef;
  @ViewChild('comparisonContainer') comparisonContainer!: ElementRef;
  @ViewChild('fileInput') fileInput!: ElementRef<HTMLInputElement>;
  
  file: File | null = null;
  fileName = '';
  data: SariMetricsResponse | null = null;
  showAlertError = false;
  errorMessage = '';
  expandedAgente: string | null = null;
  isDragging = false;
  isExporting = false;
  isExportingAll = false;
  selectedPeriodoIndex = 0;
  viewMode: 'single' | 'compare' = 'single';
  currentDate = new Date();
  analisisComparativo = '';
  monthlyCategoryChart: ApexCharts | null = null;
  private categoryComparisonChartEl: HTMLElement | null = null;
  private renderScheduleId: ReturnType<typeof setTimeout> | null = null;

  @ViewChild('categoryComparisonChart')
  set categoryComparisonChartRef(ref: ElementRef | undefined) {
    this.categoryComparisonChartEl = ref?.nativeElement ?? null;
    if (this.categoryComparisonChartEl && this.viewMode === 'compare') {
      this.scheduleMonthlyCategoryChartRender();
    }
  }

  constructor(
    private readonly _loadingService: LoadingService,
    private readonly _sariService: SariMetricsService,
    private readonly _cdr: ChangeDetectorRef
  ) {}

  onFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (input.files?.length) {
      this.processFile(input.files[0]);
    }
  }

  onDragOver(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = true;
  }

  onDragLeave(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
  }

  onDrop(event: DragEvent): void {
    event.preventDefault();
    event.stopPropagation();
    this.isDragging = false;
    
    const files = event.dataTransfer?.files;
    if (files?.length) {
      this.processFile(files[0]);
    }
  }

  private processFile(file: File): void {
    if (!file.name.toLowerCase().endsWith('.csv')) {
      this.errorMessage = 'El archivo debe ser CSV';
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
      if (this.fileInput) {
        this.fileInput.nativeElement.value = '';
      }
      return;
    }
    this.file = file;
    this.fileName = file.name;
  }

  triggerFileInput(): void {
    this.fileInput?.nativeElement.click();
  }

  clearFile(): void {
    this.file = null;
    this.fileName = '';
    this.data = null;
    this.destroyMonthlyCategoryChart();
    if (this.fileInput) {
      this.fileInput.nativeElement.value = '';
    }
  }

  upload(): void {
    if (!this.file) {
      this.errorMessage = 'Selecciona un archivo CSV';
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
      return;
    }

    this._loadingService.setLoading(true);
    this.showAlertError = false;
    this._sariService.uploadAndGetMetrics(this.file).subscribe({
      next: (response) => {
        // Filtrar "Google Workspace" del top 10
        this.filterGoogleWorkspaceFromTopUsers(response);
        this.data = response;
        if (this.viewMode === 'compare') {
          this.scheduleMonthlyCategoryChartRender();
        }
        this._loadingService.setLoading(false);
      },
      error: (err) => {
        this._loadingService.setLoading(false);
        this.errorMessage = err.error?.error?.message || err.message || 'Error al procesar el archivo';
        this.showAlertError = true;
        setTimeout(() => (this.showAlertError = false), 6000);
      }
    });
  }

  /** Filtrar "Google Workspace" del top de usuarios solicitantes */
  private filterGoogleWorkspaceFromTopUsers(response: SariMetricsResponse): void {
    if (!response?.periodos) return;
    
    response.periodos.forEach(periodo => {
      if (periodo.top_usuarios_solicitantes) {
        periodo.top_usuarios_solicitantes = periodo.top_usuarios_solicitantes.filter(
          usuario => usuario.nombre_contacto !== 'Google Workspace' &&
                     usuario.nombre_contacto !== 'Soporte Supplies de Colombia SAS' &&
                     usuario.nombre_contacto !== 'Google'
        );
      }
    });
  }

  toggleAgente(agente: string): void {
    this.expandedAgente = this.expandedAgente === agente ? null : agente;
  }

  /** Obtener el período actualmente seleccionado */
  get currentPeriodo(): PeriodoData | null {
    if (!this.data?.periodos?.length) return null;
    return this.data.periodos[this.selectedPeriodoIndex] || null;
  }

  /** Seleccionar un período específico */
  selectPeriodo(index: number): void {
    if (this.data?.periodos && index >= 0 && index < this.data.periodos.length) {
      this.selectedPeriodoIndex = index;
      this.expandedAgente = null; // Reset expanded agent
    }
  }

  /** Alternar modo de visualización */
  toggleViewMode(): void {
    this.viewMode = this.viewMode === 'single' ? 'compare' : 'single';
    if (this.viewMode === 'compare') {
      this.scheduleMonthlyCategoryChartRender();
    } else {
      this.destroyMonthlyCategoryChart();
    }
  }

  private scheduleMonthlyCategoryChartRender(): void {
    if (this.renderScheduleId !== null) {
      clearTimeout(this.renderScheduleId);
    }
    this.renderScheduleId = setTimeout(() => {
      this.renderScheduleId = null;
      this.renderMonthlyCategoryChart();
    }, 150);
  }

  get hasTotalesMensualesPorCategoria(): boolean {
    return !!this.data?.totales_mensuales_por_categoria?.length;
  }

  private formatPeriodoShort(periodo: string): string {
    const [year, month] = periodo.split('-');
    const monthNames = ['Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun', 'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'];
    const monthIndex = Number.parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  }

  private destroyMonthlyCategoryChart(): void {
    if (this.monthlyCategoryChart) {
      this.monthlyCategoryChart.destroy();
      this.monthlyCategoryChart = null;
    }
  }

  private renderMonthlyCategoryChart(): void {
    const monthlyTotals = this.data?.totales_mensuales_por_categoria;
    const chartContainer = this.categoryComparisonChartEl;
    if (!monthlyTotals?.length || !chartContainer) {
      return;
    }

    if (this.monthlyCategoryChart) {
      return;
    }

    const containerWidth = chartContainer.getBoundingClientRect().width;
    if (containerWidth === 0) {
      this.renderScheduleId = setTimeout(() => {
        this.renderScheduleId = null;
        this.renderMonthlyCategoryChart();
      }, 50);
      return;
    }

    const periodosOrdenados = [...monthlyTotals].sort((a, b) => a.periodo.localeCompare(b.periodo));
    const xCategories = periodosOrdenados.map(item => this.formatPeriodoShort(item.periodo));

    const categorias = Array.from(
      new Set(
        periodosOrdenados.flatMap(item => item.total_by_category.map(cat => cat.motivo_solicitud))
      )
    );

    const getCategoryTotal = (categoryItem: any, totalRecords: number): number => {
      if (!categoryItem) return 0;

      const numericCandidates = [
        categoryItem.total,
        categoryItem.total_tickets,
        categoryItem.tickets,
        categoryItem.count,
        categoryItem.cantidad
      ];

      for (const candidate of numericCandidates) {
        const numericValue = typeof candidate === 'number'
          ? candidate
          : Number.parseFloat(String(candidate ?? '').replace(',', '.'));
        if (Number.isFinite(numericValue)) {
          return numericValue;
        }
      }

      const percentageRaw = String(categoryItem.porcentaje ?? '').replace('%', '').replace(',', '.');
      const percentage = Number.parseFloat(percentageRaw);
      if (Number.isFinite(percentage) && Number.isFinite(totalRecords) && totalRecords > 0) {
        return (percentage / 100) * totalRecords;
      }

      return 0;
    };

    const series = categorias
      .map((categoria) => ({
        name: categoria,
        data: periodosOrdenados.map(item => {
          const categoryItem = item.total_by_category.find(cat => cat.motivo_solicitud === categoria);
          return getCategoryTotal(categoryItem, item.total_records);
        })
      }))
      .filter(serie => serie.data.some(value => value > 0));

    let visibleSet: Set<string> | null = null; // null = all visible

    const options: any = {
      chart: {
        type: 'bar' as const,
        height: 420,
        animations: { enabled: false },
        toolbar: {
          show: true,
          tools: {
            download: true,
            selection: false,
            zoom: false,
            zoomin: false,
            zoomout: false,
            pan: false,
            reset: false
          }
        },
        fontFamily: 'Montserrat, sans-serif',
        events: {
          legendClick: (_chartCtx: any, seriesIndex: number, _config: any) => {
            if (!this.monthlyCategoryChart) return;
            const clickedName = series[seriesIndex]?.name;
            if (!clickedName) return;

            if (visibleSet === null) {
              // Todas visibles → aislar solo la clickeada
              visibleSet = new Set([clickedName]);
              series.forEach(s => {
                if (s.name !== clickedName) this.monthlyCategoryChart!.hideSeries(s.name);
              });
            } else if (visibleSet.has(clickedName)) {
              // Ya visible → quitarla
              if (visibleSet.size === 1) {
                // Era la única → restaurar todas
                series.forEach(s => this.monthlyCategoryChart!.showSeries(s.name));
                visibleSet = null;
              } else {
                visibleSet.delete(clickedName);
                this.monthlyCategoryChart!.hideSeries(clickedName);
              }
            } else {
              // Estaba oculta → agregarla al conjunto visible
              visibleSet.add(clickedName);
              this.monthlyCategoryChart!.showSeries(clickedName);
            }
          }
        }
      },
      series,
      plotOptions: {
        bar: {
          horizontal: false,
          borderRadius: 0,
          columnWidth: '70%'
        }
      },
      dataLabels: {
        enabled: false
      },
      stroke: {
        show: false
      },
      fill: {
        type: 'solid',
        opacity: 1
      },
      xaxis: {
        categories: xCategories,
        title: {
          text: 'Mes'
        },
        labels: {
          style: {
            fontSize: '12px',
            fontWeight: 500
          }
        }
      },
      yaxis: {
        min: 0,
        title: {
          text: 'Tickets'
        },
        labels: {
          formatter: (value: number) => value.toLocaleString('es-CO')
        }
      },
      legend: {
        show: true,
        position: 'top',
        horizontalAlign: 'left',
        fontSize: '12px',
        onItemHover: {
          highlightDataSeries: false
        },
        onItemClick: {
          toggleDataSeries: false
        },
        markers: {
          size: 12
        },
        itemMargin: {
          horizontal: 10,
          vertical: 4
        }
      },
      states: {
        inactive: {
          opacity: 1
        },
        normal: {
          filter: {
            type: 'none'
          }
        },
        hover: {
          filter: {
            type: 'none'
          }
        },
        active: {
          filter: {
            type: 'none'
          }
        }
      },
      tooltip: {
        y: {
          formatter: (value: number) => `${value.toLocaleString('es-CO')} tickets`
        }
      },
      grid: {
        borderColor: '#e5e7eb',
        strokeDashArray: 4
      },
      colors: ['#00b894', '#0984e3', '#f39c12', '#8e44ad', '#e74c3c', '#16a085', '#6c5ce7', '#2ecc71', '#d35400']
    };

    const chart = new ApexCharts(chartContainer, options);
    this.monthlyCategoryChart = chart;
    chart.render();
  }

  /** Formatear nombre del período (ej: "Febrero 2026") */
  formatPeriodoName(periodo: string): string {
    const [year, month] = periodo.split('-');
    const monthNames = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
                        'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];
    const monthIndex = Number.parseInt(month, 10) - 1;
    return `${monthNames[monthIndex]} ${year}`;
  }

  /** Valor numérico del porcentaje para barras (quitar %) */
  porcentajeNum(val: string): number {
    const n = Number.parseFloat(val.replace('%', '').replace(',', '.'));
    return Number.isNaN(n) ? 0 : n;
  }

  /** Total de tickets por estado */
  get totalTicketsPorEstado(): number {
    if (!this.currentPeriodo?.metrics_by_state) return 0;
    return this.currentPeriodo.metrics_by_state.reduce((sum, m) => sum + m.total, 0);
  }

  /** Total de porcentajes por estado */
  get totalPorcentajePorEstado(): number {
    if (!this.currentPeriodo?.metrics_by_state) return 0;
    return this.currentPeriodo.metrics_by_state.reduce((sum, m) => sum + this.porcentajeNum(m.porcentaje), 0);
  }

  /** Total de tickets por categoría */
  get totalTicketsPorCategoria(): number {
    if (!this.currentPeriodo?.total_by_category) return 0;
    return this.currentPeriodo.total_by_category.reduce((sum, c) => sum + c.total, 0);
  }

  /** Total de porcentajes por categoría */
  get totalPorcentajePorCategoria(): number {
    if (!this.currentPeriodo?.total_by_category) return 0;
    return this.currentPeriodo.total_by_category.reduce((sum, c) => sum + this.porcentajeNum(c.porcentaje), 0);
  }

  /** Total de tickets por agente */
  get totalTicketsPorAgente(): number {
    if (!this.currentPeriodo?.hours_by_agent) return 0;
    return this.currentPeriodo.hours_by_agent.reduce((sum, h) => sum + h.numero_tickets, 0);
  }

  /** Total de porcentajes por agente */
  get totalPorcentajePorAgente(): number {
    if (!this.currentPeriodo?.hours_by_agent) return 0;
    return this.currentPeriodo.hours_by_agent.reduce((sum, h) => sum + this.porcentajeNum(h.porcentaje), 0);
  }

  /** Total de promedio de horas por agente */
  get totalPromedioHorasPorAgente(): number {
    if (!this.currentPeriodo?.hours_by_agent) return 0;
    const totalHoras = this.currentPeriodo.hours_by_agent.reduce((sum, h) => sum + h.promedio_horas, 0);
    return totalHoras;
  }

  /** Total de tickets en motivo por agente */
  get totalTicketsMotivoPorAgente(): number {
    if (!this.currentPeriodo?.motivo_por_agente) return 0;
    return this.currentPeriodo.motivo_por_agente.reduce((sum, a) => sum + a.total_tickets, 0);
  }

  /** Total de horas en motivo por agente */
  get totalHorasMotivoPorAgente(): number {
    if (!this.currentPeriodo?.motivo_por_agente) return 0;
    return this.currentPeriodo.motivo_por_agente.reduce((sum, a) => sum + a.promedio_horas, 0);
  }

  /** Total de horas en el desglose de un agente */
  getTotalPromedioHorasDesglose(desglose: MotivoPorAgenteItem[]): number {
    if (!desglose || !desglose.length) return 0;
    return desglose.reduce((sum, d) => sum + d.promedio_horas, 0);
  }

  /** Total de tickets en el desglose de un agente */
  getTotalTicketsDesglose(desglose: MotivoPorAgenteItem[]): number {
    if (!desglose || !desglose.length) return 0;
    return desglose.reduce((sum, d) => sum + d.total, 0);
  }

  /** Total de tickets por área */
  get totalTicketsPorArea(): number {
    if (!this.currentPeriodo?.analisis_division?.por_area) return 0;
    return this.currentPeriodo.analisis_division.por_area.reduce((sum, a) => sum + a.total, 0);
  }

  /** Total de porcentajes por área */
  get totalPorcentajePorArea(): number {
    if (!this.currentPeriodo?.analisis_division?.por_area) return 0;
    return this.currentPeriodo.analisis_division.por_area.reduce((sum, a) => sum + this.porcentajeNum(a.porcentaje), 0);
  }

  /** Total de tickets por tipo */
  get totalTicketsPorTipo(): number {
    if (!this.currentPeriodo?.analisis_division?.por_tipo) return 0;
    return this.currentPeriodo.analisis_division.por_tipo.reduce((sum, t) => sum + t.total, 0);
  }

  /** Total de porcentajes por tipo */
  get totalPorcentajePorTipo(): number {
    if (!this.currentPeriodo?.analisis_division?.por_tipo) return 0;
    return this.currentPeriodo.analisis_division.por_tipo.reduce((sum, t) => sum + this.porcentajeNum(t.porcentaje), 0);
  }

  /** Total de tickets por prioridad */
  get totalTicketsPorPrioridad(): number {
    if (!this.currentPeriodo?.analisis_division?.por_prioridad) return 0;
    return this.currentPeriodo.analisis_division.por_prioridad.reduce((sum, p) => sum + p.total, 0);
  }

  /** Total de porcentajes por prioridad */
  get totalPorcentajePorPrioridad(): number {
    if (!this.currentPeriodo?.analisis_division?.por_prioridad) return 0;
    return this.currentPeriodo.analisis_division.por_prioridad.reduce((sum, p) => sum + this.porcentajeNum(p.porcentaje), 0);
  }

  /** Totals for cumplimiento por agente */
  get totalCumplimientoTickets(): number {
    if (!this.currentPeriodo?.cumplimiento_por_agente) return 0;
    return this.currentPeriodo.cumplimiento_por_agente.reduce((s, a) => s + a.tickets_totales, 0);
  }

  get totalCumplimientoCerrados(): number {
    if (!this.currentPeriodo?.cumplimiento_por_agente) return 0;
    return this.currentPeriodo.cumplimiento_por_agente.reduce((s, a) => s + a.tickets_cerrados, 0);
  }

  get totalCumplimientoAbiertos(): number {
    if (!this.currentPeriodo?.cumplimiento_por_agente) return 0;
    return this.currentPeriodo.cumplimiento_por_agente.reduce((s, a) => s + a.tickets_abiertos, 0);
  }

  get totalCumplimientoEnGestion(): number {
    if (!this.currentPeriodo?.cumplimiento_por_agente) return 0;
    return this.currentPeriodo.cumplimiento_por_agente.reduce((s, a) => s + a.tickets_en_gestion, 0);
  }

  get totalPorcentajeCumplimiento(): number {
    const total = this.totalCumplimientoTickets;
    if (!total) return 0;
    return (this.totalCumplimientoCerrados / total) * 100;
  }

  /** Totals for entregables pdn por agente */
  get totalEntregables(): number {
    if (!this.currentPeriodo?.entregables_pdn_por_agente) return 0;
    return this.currentPeriodo.entregables_pdn_por_agente.reduce((s, a) => s + a.total_entregables, 0);
  }

  get totalEntregablesCumple(): number {
    if (!this.currentPeriodo?.entregables_pdn_por_agente) return 0;
    return this.currentPeriodo.entregables_pdn_por_agente.reduce((s, a) => s + a.entregables_cumple, 0);
  }

  get totalEntregablesNoCumple(): number {
    if (!this.currentPeriodo?.entregables_pdn_por_agente) return 0;
    return this.currentPeriodo.entregables_pdn_por_agente.reduce((s, a) => s + a.entregables_no_cumple, 0);
  }

  get totalPorcentajeCumplimientoEntregables(): number {
    const total = this.totalEntregables;
    if (!total) return 0;
    return (this.totalEntregablesCumple / total) * 100;
  }

  /** Color class for cumplimiento percentage */
  cumplimientoClass(porcentaje: string): string {
    const val = this.porcentajeNum(porcentaje);
    if (val >= 100) return 'cumplimiento-perfecto';
    if (val >= 90) return 'cumplimiento-alto';
    if (val >= 80) return 'cumplimiento-medio';
    return 'cumplimiento-bajo';
  }

  /** Total de tickets top usuarios solicitantes */
  get totalTopUsuariosTickets(): number {
    if (!this.currentPeriodo?.top_usuarios_solicitantes) return 0;
    return this.currentPeriodo.top_usuarios_solicitantes.reduce((s, u) => s + u.total_tickets, 0);
  }

  /** Total de porcentaje top usuarios solicitantes */
  get totalTopUsuariosPorcentaje(): number {
    if (!this.currentPeriodo?.top_usuarios_solicitantes) return 0;
    return this.currentPeriodo.top_usuarios_solicitantes.reduce((s, u) => s + this.porcentajeNum(u.porcentaje), 0);
  }

  /** Total de tickets por área (nuevo campo) */
  get totalTicketsPorAreaNew(): number {
    if (!this.currentPeriodo?.tickets_por_area) return 0;
    return this.currentPeriodo.tickets_por_area.reduce((s, a) => s + a.total_tickets, 0);
  }

  /** Total de porcentaje por área (nuevo campo) */
  get totalPorcentajePorAreaNew(): number {
    if (!this.currentPeriodo?.tickets_por_area) return 0;
    return this.currentPeriodo.tickets_por_area.reduce((s, a) => s + this.porcentajeNum(a.porcentaje), 0);
  }

  /** Valor máximo en la matriz del mapa de calor */
  get mapaCalorMax(): number {
    if (!this.currentPeriodo?.mapa_calor?.matriz?.length) return 1;
    let m = 0;
    for (const row of this.currentPeriodo.mapa_calor.matriz) {
      for (const v of row) if (v > m) m = v;
    }
    return m || 1;
  }

  /** Color de celda para mapa de calor según valor con gradiente mejorado */
  heatColor(value: number): string {
    const max = this.mapaCalorMax;
    if (max <= 0 || value <= 0) return '#f0fdfc';
    
    const p = value / max;
    
    // Gradiente de colores más visible: blanco -> turquesa claro -> turquesa -> turquesa oscuro
    if (p <= 0.2) return '#ccfbf1'; // Muy bajo
    if (p <= 0.4) return '#99f6e4'; // Bajo
    if (p <= 0.6) return '#5eead4'; // Medio
    if (p <= 0.8) return '#2dd4bf'; // Alto
    return '#14b8a6'; // Muy alto
  }

  /** Obtener clase de intensidad para el texto del mapa de calor */
  heatTextClass(value: number): string {
    if (!value) return '';
    const p = value / this.mapaCalorMax;
    return p > 0.5 ? 'text-white' : 'text-dark';
  }

  /** Calcular tasa de cierre para un período */
  getTasaCierre(periodo: PeriodoData): number {
    const cerrados = periodo.metrics_by_state.find(s => s.estado.toLowerCase() === 'cerrado');
    if (!cerrados || !periodo.total_records) return 0;
    return Number((cerrados.total / periodo.total_records * 100).toFixed(1));
  }

  /** Calcular diferencia entre dos valores enteros */
  getDifference(val1: number, val2: number): string {
    const diff = val2 - val1;
    if (diff === 0) return '0';
    return diff > 0 ? `+${diff}` : `${diff}`;
  }

  /** Calcular diferencia entre dos valores decimales */
  getDifferenceDecimal(val1: number, val2: number): string {
    const diff = val2 - val1;
    if (Math.abs(diff) < 0.1) return '0';
    return diff > 0 ? `+${diff.toFixed(1)}%` : `${diff.toFixed(1)}%`;
  }

  /** Number of weeks in a period (based on actual date range) */
  getWeeksInPeriod(periodo: PeriodoData): number {
    const start = new Date(periodo.fecha_inicio);
    let end = new Date(periodo.fecha_fin);
    if (this.data?.metadata?.fecha_max_detectada) {
      const maxDate = new Date(this.data.metadata.fecha_max_detectada);
      if (maxDate < end) end = maxDate;
    }
    const diffMs = end.getTime() - start.getTime();
    const diffDays = Math.max(1, Math.ceil(diffMs / (1000 * 60 * 60 * 24)));
    return Math.max(1, Math.ceil(diffDays / 7));
  }

  /** Weekly average tickets for a period */
  getWeeklyAverage(periodo: PeriodoData): number {
    return periodo.total_records / this.getWeeksInPeriod(periodo);
  }

  /** Peak day of week for a period */
  getPeakDay(periodo: PeriodoData): { day: string; total: number } {
    const mc = periodo.mapa_calor;
    let maxIdx = 0;
    let maxVal = 0;
    mc.totales_por_fila.forEach((v, i) => {
      if (v > maxVal) { maxVal = v; maxIdx = i; }
    });
    return { day: mc.dias_semana[maxIdx] || '', total: maxVal };
  }

  /** Peak hour for a period */
  getPeakHour(periodo: PeriodoData): { hour: number; total: number } {
    const mc = periodo.mapa_calor;
    let maxIdx = 0;
    let maxVal = 0;
    mc.totales_por_columna.forEach((v, i) => {
      if (v > maxVal) { maxVal = v; maxIdx = i; }
    });
    return { hour: mc.horas[maxIdx] || 0, total: maxVal };
  }

  /** Day-of-week comparison data between both periods */
  getDayComparisonData(): { day: string; total1: number; total2: number; pct1: number; pct2: number }[] {
    if (!this.data?.periodos || this.data.periodos.length < 2) return [];
    const p1 = this.data.periodos[0];
    const p2 = this.data.periodos[1];
    const days = p1.mapa_calor.dias_semana;
    const maxTotal = Math.max(
      ...p1.mapa_calor.totales_por_fila,
      ...p2.mapa_calor.totales_por_fila
    );
    return days.map((day, i) => ({
      day,
      total1: p1.mapa_calor.totales_por_fila[i] || 0,
      total2: p2.mapa_calor.totales_por_fila[i] || 0,
      pct1: maxTotal ? ((p1.mapa_calor.totales_por_fila[i] || 0) / maxTotal * 100) : 0,
      pct2: maxTotal ? ((p2.mapa_calor.totales_por_fila[i] || 0) / maxTotal * 100) : 0,
    }));
  }

  /** Hourly comparison data between both periods (union of all hours) */
  getHourComparisonData(): { hour: number; total1: number; total2: number; pct1: number; pct2: number }[] {
    if (!this.data?.periodos || this.data.periodos.length < 2) return [];
    const p1 = this.data.periodos[0];
    const p2 = this.data.periodos[1];
    const allHours = [...new Set([...p1.mapa_calor.horas, ...p2.mapa_calor.horas])].sort((a, b) => a - b);
    const maxTotal = Math.max(
      ...p1.mapa_calor.totales_por_columna,
      ...p2.mapa_calor.totales_por_columna
    );
    return allHours.map(hour => {
      const idx1 = p1.mapa_calor.horas.indexOf(hour);
      const idx2 = p2.mapa_calor.horas.indexOf(hour);
      const t1 = idx1 >= 0 ? p1.mapa_calor.totales_por_columna[idx1] : 0;
      const t2 = idx2 >= 0 ? p2.mapa_calor.totales_por_columna[idx2] : 0;
      return {
        hour,
        total1: t1,
        total2: t2,
        pct1: maxTotal ? (t1 / maxTotal * 100) : 0,
        pct2: maxTotal ? (t2 / maxTotal * 100) : 0,
      };
    });
  }

  /** Get weekly distribution for a period: breaks the month into calendar weeks */
  getWeeklyDistribution(periodo: PeriodoData): { label: string; days: number[]; total: number; isPartial: boolean; isMostActive: boolean }[] {
    const mc = periodo.mapa_calor;
    const start = new Date(periodo.fecha_inicio);
    let end = new Date(periodo.fecha_fin);
    if (this.data?.metadata?.fecha_max_detectada) {
      const maxDate = new Date(this.data.metadata.fecha_max_detectada);
      if (maxDate < end) end = maxDate;
    }

    // Map JS day (0=Sun) to mapa_calor index (0=lunes)
    const jsDayToIdx = (d: number) => d === 0 ? 6 : d - 1;

    // Count total occurrences of each weekday in the period
    const totalDayCounts = new Array(7).fill(0);
    const curr = new Date(start);
    while (curr <= end) {
      totalDayCounts[jsDayToIdx(curr.getDay())]++;
      curr.setDate(curr.getDate() + 1);
    }

    // Daily rate per weekday = total_for_weekday / count_of_weekday
    const dailyRate = mc.totales_por_fila.map((t: number, i: number) =>
      totalDayCounts[i] > 0 ? t / totalDayCounts[i] : 0
    );

    // Build weeks based on calendar
    const weeks: { label: string; days: number[]; total: number; isPartial: boolean; isMostActive: boolean }[] = [];
    const iter = new Date(start);
    let weekDays = new Array(7).fill(0);
    let dayCount = 0;

    while (iter <= end) {
      const idx = jsDayToIdx(iter.getDay());
      weekDays[idx] = Math.round(dailyRate[idx]);
      dayCount++;

      const next = new Date(iter);
      next.setDate(next.getDate() + 1);

      // End of week (Sunday) or end of period
      if (iter.getDay() === 0 || next > end) {
        weeks.push({
          label: `Sem ${weeks.length + 1}`,
          days: weekDays,
          total: Math.round(weekDays.reduce((s: number, v: number) => s + v, 0)),
          isPartial: dayCount < 7,
          isMostActive: false
        });
        weekDays = new Array(7).fill(0);
        dayCount = 0;
      }
      iter.setDate(iter.getDate() + 1);
    }

    // Mark the most active (non-partial preferred, else any)
    let maxTotal = 0;
    let maxIdx = 0;
    weeks.forEach((w, i) => {
      if (w.total > maxTotal) { maxTotal = w.total; maxIdx = i; }
    });
    if (weeks.length) weeks[maxIdx].isMostActive = true;

    return weeks;
  }

  /** Max weekly total across all periods for unified heatmap scale */
  getMaxWeeklyDayValue(): number {
    if (!this.data?.periodos) return 1;
    let max = 0;
    for (const p of this.data.periodos) {
      const weeks = this.getWeeklyDistribution(p);
      for (const w of weeks) {
        for (const d of w.days) {
          if (d > max) max = d;
        }
      }
    }
    return max || 1;
  }

  /** Color for weekly heatmap cells */
  weeklyHeatColor(value: number): string {
    const max = this.getMaxWeeklyDayValue();
    if (max <= 0 || value <= 0) return '#f0fdfc';
    const p = value / max;
    if (p <= 0.2) return '#ccfbf1';
    if (p <= 0.4) return '#99f6e4';
    if (p <= 0.6) return '#5eead4';
    if (p <= 0.8) return '#2dd4bf';
    return '#14b8a6';
  }

  /** Text class for weekly heatmap cells */
  weeklyHeatTextClass(value: number): string {
    if (!value) return '';
    const p = value / this.getMaxWeeklyDayValue();
    return p > 0.5 ? 'text-white' : 'text-dark';
  }

  /** Max week rows across periods (for table alignment) */
  getMaxWeeks(): number {
    if (!this.data?.periodos) return 0;
    return Math.max(...this.data.periodos.map(p => this.getWeeklyDistribution(p).length));
  }

  /** Wait for Angular to render after state change */
  private waitForRender(): Promise<void> {
    return new Promise(resolve => {
      this._cdr.detectChanges();
      requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
    });
  }

  /** Capture an element as canvas */
  private captureElement(element: HTMLElement): Promise<HTMLCanvasElement> {
    return html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff',
      windowWidth: element.scrollWidth,
      windowHeight: element.scrollHeight,
      width: element.scrollWidth,
      height: element.scrollHeight,
      scrollX: 0,
      scrollY: 0
    });
  }

  /** Add canvas image to PDF slicing into pages properly */
  private addCanvasToPDF(pdf: jsPDF, canvas: HTMLCanvasElement, startY: number, sectionTitle: string): number {
    const pageWidth = 297; // A4 landscape
    const pageHeight = 210;
    const margin = 10;
    const contentWidth = pageWidth - (margin * 2);
    const contentHeight = pageHeight - (margin * 2);

    // Section title
    pdf.setFontSize(14);
    pdf.setTextColor(0, 70, 70);
    pdf.text(sectionTitle, margin, startY);
    startY += 8;

    const imgWidth = contentWidth;
    const imgHeight = (canvas.height * imgWidth) / canvas.width;

    // How much vertical space is left on the current page
    let availableOnPage = pageHeight - margin - startY;
    let drawnHeight = 0;

    while (drawnHeight < imgHeight) {
      const sliceHeight = Math.min(availableOnPage, imgHeight - drawnHeight);

      // Crop the portion of the canvas for this page
      const srcY = (drawnHeight / imgHeight) * canvas.height;
      const srcH = (sliceHeight / imgHeight) * canvas.height;

      const sliceCanvas = document.createElement('canvas');
      sliceCanvas.width = canvas.width;
      sliceCanvas.height = Math.ceil(srcH);
      const ctx = sliceCanvas.getContext('2d');
      if (ctx) {
        ctx.drawImage(canvas, 0, srcY, canvas.width, srcH, 0, 0, canvas.width, srcH);
      }

      const sliceData = sliceCanvas.toDataURL('image/png');
      pdf.addImage(sliceData, 'PNG', margin, startY, imgWidth, sliceHeight);

      drawnHeight += sliceHeight;

      if (drawnHeight < imgHeight) {
        pdf.addPage();
        startY = margin;
        availableOnPage = contentHeight;
      }
    }

    // Return the Y position after the last drawn content
    return startY + (imgHeight - (drawnHeight - Math.min(availableOnPage, imgHeight - (drawnHeight - availableOnPage))));
  }

  async exportToPDF(): Promise<void> {
    if (!this.data) return;

    // Save current state
    const savedIndex = this.selectedPeriodoIndex;
    const savedViewMode = this.viewMode;
    const savedExpanded = this.expandedAgente;

    this.isExporting = true;
    this.isExportingAll = true;

    try {
      const pdf = new jsPDF('l', 'mm', 'a4');
      const margin = 10;

      // Header info on first page
      let cursorY = margin;
      pdf.setFontSize(18);
      pdf.setTextColor(0, 70, 70);
      pdf.text('Métricas SARI - Reporte', margin, cursorY);
      cursorY += 8;

      pdf.setFontSize(10);
      pdf.setTextColor(102, 102, 102);
      const fecha = new Date().toLocaleDateString('es-ES', {
        year: 'numeric',
        month: 'long',
        day: 'numeric'
      });
      pdf.text(`Generado: ${fecha}`, margin, cursorY);
      cursorY += 5;
      if (this.data.metadata.filename) {
        pdf.text(`Archivo: ${this.data.metadata.filename}`, margin, cursorY);
        cursorY += 5;
      }
      pdf.text(`Total tickets analizados: ${this.data.total_general}`, margin, cursorY);
      cursorY += 8;

      // Capture each period — first one starts right below header
      let isFirst = true;
      for (let i = 0; i < this.data.periodos.length; i++) {
        this.selectedPeriodoIndex = i;
        this.viewMode = 'single';
        await this.waitForRender();

        if (this.resultsContainer) {
          const canvas = await this.captureElement(this.resultsContainer.nativeElement);
          if (!isFirst) {
            pdf.addPage();
            cursorY = margin;
          }
          this.addCanvasToPDF(pdf, canvas, cursorY, this.formatPeriodoName(this.data.periodos[i].periodo));
          isFirst = false;
        }
      }

      // Capture comparison view (if 2 periods)
      if (this.data.periodos.length === 2) {
        this.viewMode = 'compare';
        await this.waitForRender();
        this.renderMonthlyCategoryChart();
        await this.waitForRender();

        if (this.comparisonContainer) {
          const canvas = await this.captureElement(this.comparisonContainer.nativeElement);
          pdf.addPage();
          this.addCanvasToPDF(pdf, canvas, margin, 'Comparación de períodos');
        }
      }

      // Save PDF
      const fileName = this.fileName.replace('.csv', '') || 'metricas-sari';
      pdf.save(`${fileName}-reporte.pdf`);
    } catch (error) {
      console.error('Error al exportar PDF:', error);
      this.errorMessage = 'Error al exportar el PDF';
      this.showAlertError = true;
      setTimeout(() => (this.showAlertError = false), 5000);
    } finally {
      // Restore original state
      this.selectedPeriodoIndex = savedIndex;
      this.viewMode = savedViewMode;
      this.expandedAgente = savedExpanded;
      this.isExportingAll = false;
      this.isExporting = false;
      this._cdr.detectChanges();
      if (this.viewMode === 'compare') {
        this.scheduleMonthlyCategoryChartRender();
      } else {
        this.destroyMonthlyCategoryChart();
      }
    }
  }

  ngOnDestroy(): void {
    this.destroyMonthlyCategoryChart();
  }
}
